import React from 'react';
import { Shield, Lock, Eye, FileText, Database, UserCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center bg-amber-500/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <Shield className="h-4 w-4 text-amber-500 mr-2" />
              <span className="text-sm text-amber-500 font-medium">Your Privacy Matters</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Privacy <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Policy</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Last Updated: January 2025
            </p>
          </div>
        </div>
      </section>

      {/* Quick Overview */}
      <section className="py-12 bg-slate-900/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="pt-6 text-center">
                <Lock className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Secure Data</h3>
                <p className="text-gray-400 text-sm">Your information is encrypted and protected</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="pt-6 text-center">
                <Eye className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Transparent</h3>
                <p className="text-gray-400 text-sm">Clear about what we collect and why</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="pt-6 text-center">
                <UserCheck className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Your Control</h3>
                <p className="text-gray-400 text-sm">You own and control your data</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert max-w-none">
            <div className="space-y-12">
              {/* Introduction */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Introduction</h2>
                <p className="text-gray-300 leading-relaxed">
                  At Global Filing Protection®, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
                </p>
              </div>

              {/* Information We Collect */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Database className="h-6 w-6 text-amber-500 mr-3" />
                  Information We Collect
                </h2>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-6 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Personal Information</h3>
                    <p className="text-gray-300">We collect personal information that you voluntarily provide to us when you:</p>
                    <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1 ml-4">
                      <li>Fill out contact forms or request consultations</li>
                      <li>Register for trademark services</li>
                      <li>Subscribe to newsletters or communications</li>
                      <li>Contact our customer support</li>
                    </ul>
                    <p className="text-gray-300 mt-3">This may include: name, email address, phone number, business information, and trademark-related details.</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Automatically Collected Information</h3>
                    <p className="text-gray-300">When you visit our website, we automatically collect certain information about your device, including:</p>
                    <ul className="list-disc list-inside text-gray-300 mt-2 space-y-1 ml-4">
                      <li>IP address and browser type</li>
                      <li>Pages visited and time spent on pages</li>
                      <li>Referring website addresses</li>
                      <li>Device and operating system information</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* How We Use Your Information */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <FileText className="h-6 w-6 text-amber-500 mr-3" />
                  How We Use Your Information
                </h2>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-6">
                  <p className="text-gray-300 mb-4">We use the information we collect to:</p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>Provide, maintain, and improve our trademark services</li>
                    <li>Process your trademark applications and related requests</li>
                    <li>Communicate with you about services, updates, and promotional offers</li>
                    <li>Respond to your inquiries and provide customer support</li>
                    <li>Monitor and analyze usage patterns to enhance user experience</li>
                    <li>Detect, prevent, and address technical issues or fraudulent activity</li>
                    <li>Comply with legal obligations and protect our legal rights</li>
                  </ul>
                </div>
              </div>

              {/* Data Security */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Lock className="h-6 w-6 text-amber-500 mr-3" />
                  Data Security
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="list-disc list-inside text-gray-300 mt-3 space-y-2 ml-4">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Regular security assessments and updates</li>
                  <li>Restricted access to personal information on a need-to-know basis</li>
                  <li>Secure backup and disaster recovery procedures</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.
                </p>
              </div>

              {/* Information Sharing */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Information Sharing and Disclosure</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                </p>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-6 space-y-3">
                  <div>
                    <h3 className="text-white font-semibold mb-1">Service Providers</h3>
                    <p className="text-gray-300 text-sm">With trusted third-party service providers who assist us in operating our website and services, subject to confidentiality obligations.</p>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Legal Requirements</h3>
                    <p className="text-gray-300 text-sm">When required by law or to protect our rights, property, or safety, or that of others.</p>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Business Transfers</h3>
                    <p className="text-gray-300 text-sm">In connection with a merger, acquisition, or sale of assets, where your information may be transferred as part of the transaction.</p>
                  </div>
                </div>
              </div>

              {/* Your Rights */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Your Rights and Choices</h2>
                <p className="text-gray-300 leading-relaxed mb-4">You have the right to:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>Access, update, or delete your personal information</li>
                  <li>Opt-out of marketing communications at any time</li>
                  <li>Request a copy of the data we hold about you</li>
                  <li>Object to or restrict certain processing of your data</li>
                  <li>Withdraw consent where we rely on consent as the legal basis for processing</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  To exercise these rights, please contact us at info@globalfilingprotection.com.
                </p>
              </div>

              {/* Cookies */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Cookies and Tracking Technologies</h2>
                <p className="text-gray-300 leading-relaxed">
                  We use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and understand user preferences. You can control cookie settings through your browser, but disabling cookies may affect the functionality of our website.
                </p>
              </div>

              {/* Children's Privacy */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Children's Privacy</h2>
                <p className="text-gray-300 leading-relaxed">
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child without parental consent, we will take steps to delete that information.
                </p>
              </div>

              {/* Changes to Policy */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Changes to This Privacy Policy</h2>
                <p className="text-gray-300 leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this policy periodically.
                </p>
              </div>

              {/* Contact */}
              <div className="bg-slate-900/60 border border-white/20 rounded-lg p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-2 text-gray-300">
                  <p><strong className="text-white">Email:</strong> info@globalfilingprotection.com</p>
                  <p><strong className="text-white">Phone:</strong> +1-833-202-0812</p>
                  <p><strong className="text-white">Address:</strong> 12100 Wilshire Blvd 8th floor, Los Angeles, CA 90025</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
